(() => {
  const liveRegion = () => document.querySelector('.bhc-live-region');

  const announce = (message) => {
    const region = liveRegion();
    if (region) region.textContent = message;
  };

  let lastHeaderSearchTrigger = null;
  let lastFreeShippingDialogTrigger = null;

  const closeHeaderSearch = ({ restoreFocus = false } = {}) => {
    const panel = document.querySelector('[data-header-search-panel]');
    const toggle = document.querySelector('[data-header-search-toggle]');
    if (!panel || !panel.hasAttribute('open')) return;

    panel.removeAttribute('open');
    toggle?.setAttribute('aria-expanded', 'false');
    announce('Header search closed');

    if (restoreFocus) {
      (lastHeaderSearchTrigger || toggle)?.focus();
    }
  };

  const openHeaderSearch = (toggle) => {
    const panel = document.querySelector('[data-header-search-panel]');
    const input = document.querySelector('[data-header-search-input]');
    if (!panel) return;

    lastHeaderSearchTrigger = toggle;
    panel.setAttribute('open', '');
    toggle?.setAttribute('aria-expanded', 'true');
    announce('Header search opened');
    window.requestAnimationFrame(() => input?.focus());
  };

  const setProductMedia = (gallery, mediaId) => {
    if (!gallery || !mediaId) return;

    const featured = gallery.querySelector('[data-product-featured-media]');
    const template = [...gallery.querySelectorAll('[data-product-media-template]')]
      .find((candidate) => candidate.dataset.productMediaTemplate === String(mediaId));

    if (!featured || !template) return;

    featured.replaceChildren(template.content.cloneNode(true));
    featured.dataset.currentMediaId = String(mediaId);

    gallery.querySelectorAll('[data-product-media-trigger]').forEach((trigger) => {
      const isActive = trigger.dataset.mediaId === String(mediaId);
      trigger.classList.toggle('is-active', isActive);
      if (isActive) {
        trigger.setAttribute('aria-current', 'true');
      } else {
        trigger.removeAttribute('aria-current');
      }
    });

    announce('Product image updated');
  };

  let mobileMenuTrigger = null;
  let mobileMenuBackground = [];

  const closeMobileMenu = (drawer, restoreFocus = true) => {
    if (!drawer?.hasAttribute('open')) return;
    drawer.removeAttribute('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('bhc-menu-open');
    mobileMenuBackground.forEach((node) => { node.inert = false; });
    mobileMenuBackground = [];
    document.querySelector('[data-open-mobile-drawer]')?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && mobileMenuTrigger?.isConnected) mobileMenuTrigger.focus();
  };

  const openMobileMenu = (drawer, trigger) => {
    closeHeaderSearch();
    mobileMenuTrigger = trigger;
    drawer.setAttribute('open', '');
    drawer.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('bhc-menu-open');
    mobileMenuBackground = [...document.querySelectorAll('main, footer, .header, .announcement-bar')]
      .filter((node) => !node.contains(drawer) && !node.inert);
    mobileMenuBackground.forEach((node) => { node.inert = true; });
    drawer.querySelector('[data-close-mobile-drawer]')?.focus();
  };

  window.matchMedia('(min-width: 1181px)').addEventListener('change', (event) => {
    if (event.matches) closeMobileMenu(document.querySelector('[data-mobile-drawer]'), false);
  });

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...container.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.disabled && node.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const freeShippingDialogKey = (dialog) => {
    return `bhc-free-shipping-dialog:${dialog.dataset.threshold || 'default'}`;
  };

  const closeFreeShippingDialog = (dialog, { restoreFocus = true } = {}) => {
    if (!dialog || !dialog.hasAttribute('open')) return;
    dialog.removeAttribute('open');
    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');
    announce('Free shipping message closed');

    if (restoreFocus) {
      lastFreeShippingDialogTrigger?.focus();
    }
  };

  const openFreeShippingDialog = (dialog, trigger = null, { automatic = false } = {}) => {
    if (!dialog) return;

    if (automatic && dialog.dataset.frequency === 'once_per_session') {
      try {
        const storageKey = freeShippingDialogKey(dialog);
        if (window.sessionStorage.getItem(storageKey) === 'shown') return;
        window.sessionStorage.setItem(storageKey, 'shown');
      } catch {}
    }

    lastFreeShippingDialogTrigger = trigger || document.activeElement;
    dialog.hidden = false;
    dialog.setAttribute('open', '');
    dialog.removeAttribute('aria-hidden');
    announce('Free shipping message opened');

    window.requestAnimationFrame(() => {
      const panel = dialog.querySelector('[role="dialog"]');
      const initialFocus = panel?.querySelector('[data-free-shipping-dialog-close], a, button');
      (initialFocus || panel)?.focus();
    });
  };

  const initFreeShippingDialogs = (root = document) => {
    root.querySelectorAll('[data-free-shipping-dialog][data-auto-open="true"]').forEach((dialog) => {
      if (dialog.dataset.bhcDialogInitialized === 'true') return;
      dialog.dataset.bhcDialogInitialized = 'true';
      window.setTimeout(() => openFreeShippingDialog(dialog, null, { automatic: true }), 700);
    });
  };

  document.addEventListener('click', (event) => {
    const searchToggle = event.target.closest('[data-header-search-toggle]');
    const searchPanel = document.querySelector('[data-header-search-panel]');
    const openDrawer = event.target.closest('[data-open-mobile-drawer]');
    const closeDrawer = event.target.closest('[data-close-mobile-drawer]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    const mediaTrigger = event.target.closest('[data-product-media-trigger]');
    const freeShippingDialogTrigger = event.target.closest('[data-free-shipping-dialog-open]');
    const freeShippingDialogClose = event.target.closest('[data-free-shipping-dialog-close]');

    if (mediaTrigger) {
      setProductMedia(mediaTrigger.closest('[data-product-media-gallery]'), mediaTrigger.dataset.mediaId);
    }

    if (freeShippingDialogTrigger) {
      event.preventDefault();
      const dialog = document.getElementById(freeShippingDialogTrigger.getAttribute('aria-controls'));
      openFreeShippingDialog(dialog, freeShippingDialogTrigger);
    }

    if (freeShippingDialogClose) {
      closeFreeShippingDialog(freeShippingDialogClose.closest('[data-free-shipping-dialog]'));
    }

    if (searchToggle) {
      const isOpen = searchPanel?.hasAttribute('open');
      if (isOpen) {
        closeHeaderSearch({ restoreFocus: true });
      } else {
        openHeaderSearch(searchToggle);
      }
    } else if (searchPanel?.hasAttribute('open') && !searchPanel.contains(event.target)) {
      closeHeaderSearch();
    }

    if (openDrawer && drawer) {
      openMobileMenu(drawer, openDrawer);
    }

    if ((closeDrawer || event.target === drawer) && drawer) {
      closeMobileMenu(drawer);
    }

    if (drawer?.hasAttribute('open') && event.target.closest('[data-mobile-drawer] a[href]')) {
      closeMobileMenu(drawer, false);
    }

    const accordion = event.target.closest('[data-accordion-button]');
    if (accordion) {
      const expanded = accordion.getAttribute('aria-expanded') === 'true';
      accordion.setAttribute('aria-expanded', String(!expanded));
    }

    const filterToggle = event.target.closest('[data-filter-toggle]');
    if (filterToggle) {
      const panel = document.querySelector('[data-filter-panel]');
      if (panel) {
        panel.classList.toggle('is-open');
        const isOpen = panel.classList.contains('is-open');
        filterToggle.setAttribute('aria-expanded', String(isOpen));
        announce(isOpen ? 'Filters opened' : 'Filters closed');
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    const freeShippingDialog = document.querySelector('[data-free-shipping-dialog][open]');
    if (freeShippingDialog) {
      if (event.key === 'Escape') {
        closeFreeShippingDialog(freeShippingDialog);
      } else {
        trapFocus(freeShippingDialog, event);
      }
      return;
    }

    const headerSearchPanel = document.querySelector('[data-header-search-panel][open]');
    if (headerSearchPanel && event.key === 'Escape') {
      closeHeaderSearch({ restoreFocus: true });
      return;
    }

    const drawer = document.querySelector('[data-mobile-drawer][open]');
    if (drawer) {
      if (event.key === 'Escape') {
        closeMobileMenu(drawer);
      } else {
        trapFocus(drawer, event);
      }
    }

    const filterPanel = document.querySelector('[data-filter-panel].is-open');
    if (filterPanel && event.key === 'Escape') {
      filterPanel.classList.remove('is-open');
      document.querySelector('[data-filter-toggle]')?.focus();
    }
  });

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    const fixed = value.toFixed(2);
    const noDecimals = String(Math.round(value));
    const withComma = fixed.replace('.', ',');
    const noDecimalsWithComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const format = window.themeMoneyFormat || '${{amount}}';

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalsWithComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, fixed);
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const renderVariantPrice = (priceNode, variant) => {
    const priceText = formatMoney(variant.price);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const variantPrice = Number(variant.price || 0);

    if (compareAtPrice > variantPrice) {
      const saleLabel = escapeHtml(priceNode?.dataset.salePriceLabel || 'Sale price');
      const regularLabel = escapeHtml(priceNode?.dataset.regularPriceLabel || 'Regular price');
      return `<span class="visually-hidden">${saleLabel}</span><span>${priceText}</span><span class="visually-hidden">${regularLabel}</span><s>${formatMoney(compareAtPrice)}</s>`;
    }

    return `<span>${priceText}</span>`;
  };

  const initProductForm = (root) => {
    const variantsScript = root.querySelector('[data-product-variants]');
    const idInput = root.querySelector('input[name="id"]');
    const addButton = root.querySelector('[data-add-button]');
    const price = root.querySelector('[data-product-price]');
    if (!variantsScript || !idInput) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      return;
    }

    const quantityInput = root.querySelector('.quantity-stepper input[name="quantity"]');
    const updateQuantity = (delta) => {
      if (!quantityInput) return;
      const minimum = Number(quantityInput.min || 1);
      const maximum = quantityInput.max ? Number(quantityInput.max) : Number.POSITIVE_INFINITY;
      const current = Number(quantityInput.value || minimum);
      quantityInput.value = String(Math.min(maximum, Math.max(minimum, current + delta)));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    root.querySelector('[data-quantity-decrease]')?.addEventListener('click', () => updateQuantity(-1));
    root.querySelector('[data-quantity-increase]')?.addEventListener('click', () => updateQuantity(1));

    const reviewCta = root.querySelector('[data-okendo-review-cta]');
    const reviewAppSlot = root.querySelector('[data-okendo-widget="okendo-star-rating"]');
    if (reviewCta && reviewAppSlot) {
      const syncReviewLink = () => {
        const okendoLink = reviewAppSlot.querySelector('a[href*="okendo.reviews"]');
        if (!okendoLink) return false;

        reviewCta.href = okendoLink.href;
        reviewCta.target = okendoLink.target || '_blank';
        reviewCta.rel = 'noopener';
        return true;
      };

      if (!syncReviewLink()) {
        const observer = new MutationObserver(() => {
          if (syncReviewLink()) observer.disconnect();
        });
        observer.observe(reviewAppSlot, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
        window.setTimeout(() => observer.disconnect(), 10000);
      }
    }

    reviewCta?.addEventListener('click', (event) => {
      if (!reviewCta.getAttribute('href')?.startsWith('#')) return;

      const sectionReviewCta = document.querySelector('[data-okendo-section-review-cta]');
      if (sectionReviewCta instanceof HTMLAnchorElement && sectionReviewCta.href.startsWith('https://okendo.reviews/')) {
        event.preventDefault();
        sectionReviewCta.click();
        return;
      }

      const nativeWriteReview = document.querySelector('.oke-w-writeReview, [data-oke-action="write-review"], .okeReviews button[class*="writeReview"]');
      if (nativeWriteReview instanceof HTMLElement) {
        event.preventDefault();
        nativeWriteReview.click();
      }
    });

    const update = () => {
      const selected = [...root.querySelectorAll('[data-option-position]')].map((fieldset) => {
        return fieldset.querySelector('input:checked')?.value;
      });
      const variant = variants.find((candidate) => {
        return selected.every((value, index) => candidate.options[index] === value);
      });

      if (!variant) {
        addButton && (addButton.disabled = true);
        addButton && (addButton.textContent = addButton.dataset.unavailableText || 'Unavailable');
        return;
      }

      idInput.value = variant.id;
      if (price) price.innerHTML = renderVariantPrice(price, variant);
      if (variant.featured_media?.id) {
        setProductMedia(root.closest('.product-main')?.querySelector('[data-product-media-gallery]'), variant.featured_media.id);
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available
          ? (addButton.dataset.availableText || 'Add to cart')
          : (addButton.dataset.soldOutText || 'Sold out');
      }
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input]')) update();
    });
    update();
  };

  const initOkendoReviewSections = (root = document) => {
    root.querySelectorAll('[data-okendo-widget="okendo-reviews-widget"]').forEach((shell) => {
      if (shell.dataset.bhcReviewsInitialized === 'true') return;
      shell.dataset.bhcReviewsInitialized = 'true';

      const reviewCta = shell.querySelector('[data-okendo-section-review-cta]');
      const appSlot = shell.querySelector('.okendo-widget-shell__app-slot');
      if (!(reviewCta instanceof HTMLAnchorElement) || !appSlot) return;

      const setReviewState = (reviewCount) => {
        const hasReviews = Number(reviewCount) > 0;
        shell.classList.toggle('okendo-widget-shell--has-reviews', hasReviews);
        shell.classList.toggle('okendo-widget-shell--empty', !hasReviews);
      };

      const syncReviewCta = () => {
        const nativeReviewLink = appSlot.querySelector('a.oke-w-writeReview[href*="okendo.reviews"]');
        const dataScript = appSlot.querySelector('script[data-oke-metafield-data]');
        let reviewData = null;

        if (dataScript?.textContent) {
          try {
            reviewData = JSON.parse(dataScript.textContent);
          } catch {}
        }

        const aggregate = reviewData?.reviewAggregate;
        if (aggregate) {
          setReviewState(aggregate.ratingAndReviewCount ?? aggregate.ratingCount ?? 0);
        }

        let reviewUrl = nativeReviewLink?.href;
        if (!reviewUrl && aggregate?.subscriberId && aggregate?.productId) {
          const url = new URL('https://okendo.reviews/');
          url.searchParams.set('subscriberId', aggregate.subscriberId);
          url.searchParams.set('productId', aggregate.productId);
          url.searchParams.set('locale', document.documentElement.lang || 'en');
          url.searchParams.set('okeChannel', 'reviews-widget-write-review-button');
          reviewUrl = url.toString();
        }

        if (!reviewUrl) return false;

        reviewCta.href = reviewUrl;
        reviewCta.target = nativeReviewLink?.target || '_blank';
        reviewCta.rel = 'noopener';
        reviewCta.removeAttribute('aria-disabled');
        return true;
      };

      if (!syncReviewCta()) {
        const observer = new MutationObserver(() => {
          if (syncReviewCta()) observer.disconnect();
        });
        observer.observe(appSlot, { childList: true, subtree: true });
        window.setTimeout(() => observer.disconnect(), 10000);
      }

      reviewCta.addEventListener('click', (event) => {
        if (reviewCta.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
    });
  };

  document.querySelectorAll('[data-product-form-root]').forEach(initProductForm);
  initOkendoReviewSections();
  initFreeShippingDialogs();

  const prefersReducedMotion = () => {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  };

  const prefersReducedData = () => {
    return navigator.connection?.saveData === true;
  };

  const shouldSkipAutoplayVideo = (video) => {
    return video?.dataset.bhcVideoAutoplay === 'true' && (prefersReducedMotion() || prefersReducedData());
  };

  const loadDeferredVideo = (video, { play = false } = {}) => {
    if (!video || video.dataset.bhcVideoLoaded === 'true') return;
    if (!video.getClientRects().length) return;
    const matchingSource = [...video.querySelectorAll('source[data-src]')].some(source =>
      !source.media || window.matchMedia(source.media).matches);
    if (!matchingSource) return;

    const sources = [...video.querySelectorAll('source[data-src]')];
    sources.forEach((source) => {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
    });

    video.dataset.bhcVideoLoaded = 'true';
    video.load();

    const markLoaded = () => video.classList.add('is-loaded');
    if (video.readyState >= 2) {
      markLoaded();
    } else {
      video.addEventListener('loadeddata', markLoaded, { once: true });
      video.addEventListener('canplay', markLoaded, { once: true });
    }

    if (play || video.dataset.bhcVideoAutoplay === 'true') {
      video.play().then(markLoaded).catch(() => {});
    }
  };

  const bindDeferredVideoClick = (video) => {
    if (!video || video.dataset.bhcVideoClickBound === 'true') return;

    video.dataset.bhcVideoClickBound = 'true';
    const container = video.closest('[data-bhc-video-container]') || video.parentElement;
    const playButton = container?.querySelector('[data-bhc-video-play]');

    const playVideo = (event) => {
      event?.preventDefault();
      playButton?.setAttribute('hidden', '');
      loadDeferredVideo(video, { play: true });
    };

    playButton?.addEventListener('click', playVideo);
    video.addEventListener('click', () => {
      if (video.dataset.bhcVideoLoaded !== 'true') playVideo();
    });
  };

  const scheduleDeferredVideos = (root = document) => {
    const videos = [...root.querySelectorAll('video[data-bhc-deferred-video]')];
    if (!videos.length) return;

    const viewportVideos = [];

    videos.forEach((video) => {
      const mode = video.dataset.bhcVideoLoadMode || 'viewport';
      if (shouldSkipAutoplayVideo(video) && mode !== 'click') {
        video.dataset.bhcVideoSkipped = 'true';
        return;
      }

      if (mode === 'click') {
        bindDeferredVideoClick(video);
        return;
      }

      if (mode === 'delay') {
        if (video.dataset.bhcVideoScheduled === 'true') return;
        video.dataset.bhcVideoScheduled = 'true';
        const delay = Number(video.dataset.bhcVideoDelay || 0);
        const safeDelay = Number.isFinite(delay) ? Math.max(delay, 25000) : 25000;
        window.setTimeout(() => loadDeferredVideo(video), safeDelay);
        return;
      }

      if (mode === 'immediate') {
        loadDeferredVideo(video);
        return;
      }

      viewportVideos.push(video);
    });

    if (!viewportVideos.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadDeferredVideo(entry.target);
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '900px 0px' });

      viewportVideos.forEach((video) => observer.observe(video));
    } else {
      viewportVideos.forEach(loadDeferredVideo);
    }
  };

  const loadDeferredMap = (loader) => {
    if (!loader || loader.dataset.bhcMapLoaded === 'true') return;
    const src = loader.dataset.bhcMapSrc;
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.className = 'local-store__map-iframe';
    iframe.src = src;
    iframe.title = loader.dataset.bhcMapTitle || 'Store location map';
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'no-referrer-when-downgrade';
    iframe.allowFullscreen = true;

    loader.dataset.bhcMapLoaded = 'true';
    loader.replaceWith(iframe);
  };

  const initDeferredMaps = (root = document) => {
    root.querySelectorAll('[data-bhc-map-loader]').forEach((loader) => {
      if (loader.dataset.bhcMapBound === 'true') return;
      loader.dataset.bhcMapBound = 'true';
      loader.addEventListener('click', () => loadDeferredMap(loader));
    });
  };

  const runWhenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1800 });
    } else {
      window.setTimeout(callback, 1200);
    }
  };

  const initDeferredVideos = (root = document) => runWhenIdle(() => scheduleDeferredVideos(root));

  initDeferredMaps();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initDeferredVideos(), { once: true });
  } else {
    initDeferredVideos();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-form-root]').forEach(initProductForm);
    initOkendoReviewSections(event.target);
    initFreeShippingDialogs(event.target);
    initDeferredMaps(event.target);
    initDeferredVideos(event.target);
  });
})();
